<template>
	<ja-modal
		:show="isModalVisibleInShopping || isModalVisibleInBudgeting"
		class="info-modal"
	>
		<ja-close-button
			name="close"
			@click.native="closeModal()"
		></ja-close-button>
		<div class="modal-content">
			<h3
				v-if="defaultOptions.hasTitle"
				class="modal__title heading-3 text--center"
			>
				{{ defaultOptions.title }}
			</h3>
			<perfect-scrollbar
				class="content text--center"
				:class="{ 'mt--4': !defaultOptions.hasTitle }"
			>
				<div
					v-if="isModalVisibleInBudgeting"
					class="body-text-1 text--md pb--2"
				>
					<p>
						{{
							$tc('haveToApplyForLoanInfo', loanMessage.count, {
								loan: loanMessage.type
							})
						}}
					</p>
				</div>
				<div
					v-if="isModalVisibleInShopping"
					class="body-text-1 text--md  pb--2"
				>
					<p
						v-if="isCongratsVisible"
						v-html="
							$t(`loanStatusMessages.${loanMessage.loanTypeStatus}`, {
								autoLoanAmount: loansAmount.autoLoanBankingAndFinance,
								autoLoanDealershipAmount: loansAmount.autoLoan,
								mortgageLoanAmount: loansAmount.mortgageLoan
							})
						"
					></p>
					<p
						v-if="isDisclaimerVisible"
						v-html="$t('loanStatusMessages.disclaimer')"
					></p>
				</div>
			</perfect-scrollbar>
		</div>

		<div v-if="defaultOptions.hasButtons" class="btn-group btn-group--center">
			<button name="confirm" class="btn btn-link" @click="closeModal()">
				{{ defaultOptions.confirmText || $t('close') }}
			</button>

			<ja-button
				v-if="defaultOptions.hasCancel"
				name="cancel"
				class="small outlined"
				@click.native="cancel()"
			>
				{{ defaultOptions.cancelText || $t('cancel') }}
			</ja-button>
		</div>
	</ja-modal>
</template>

<script>
import Modal from '@/components/Modal/Modal';
import { CloseButton, Button } from '@ui';
import { LifeStageData, UserData } from '@/mixins';

export default {
	components: {
		jaModal: Modal,
		jaCloseButton: CloseButton,
		jaButton: Button
	},
	mixins: [LifeStageData, UserData],
	props: {
		show: {
			type: Boolean,
			default: false
		},
		options: {
			type: Object,
			default: () => {}
		}
	},
	data() {
		return {
			defaultOptions: {
				hasButtons: true,
				hasTitle: false,
				title: 'Information',
				hasCancel: false,
				cancelText: '',
				confirmText: ''
			},
			showModal: true
		};
	},
	computed: {
		loanMessage() {
			// changes in the getters will trigger this computed
			const { isAutoLoanApplied, isMortgageLoanApplied } = this.$store.getters;
			if (this.isModalVisibleInBudgeting) {
				return this.getMessageDataForBudgeting(
					isAutoLoanApplied,
					isMortgageLoanApplied
				);
			}
			return this.getMessageDataForShopping(
				isAutoLoanApplied,
				isMortgageLoanApplied
			);
		},
		// Set here to simpfily other methods where it is used
		hasAnyLoanApplied() {
			const {
				isAutoLoanDealershipVisible,
				isAutoLoanFinanceVisible,
				isAutoLoanApplied,
				isMortgageLoanVisible,
				isMortgageLoanApplied
			} = this.$store.getters;
			return (
				((isAutoLoanDealershipVisible || isAutoLoanFinanceVisible) &&
					isAutoLoanApplied) ||
				(isMortgageLoanVisible && isMortgageLoanApplied)
			);
		},
		// Set here to simpfily other methods where it is used
		hasToShowAnyLoan() {
			return (
				this.autoLoan.loanDisplayedInShoppingPhase ||
				this.autoLoanBankingAndFinance.loanDisplayedInShoppingPhase ||
				this.mortgageLoan.loanDisplayedInShoppingPhase
			);
		},
		isModalVisibleInBudgeting() {
			// TODO: Simplify this fragment here
			return (
				this.$store.getters.areAllBudgetsSet &&
				this.isInBudgetingPhase &&
				// Show modal after budgeting screen
				this.userData.hideBudgetPopupOnHomeScreen &&
				this.isLoanRequired &&
				(this.hasAutoLoanTriggerLoansModal ||
					this.hasMortgageLoanTriggerLoansModal) &&
				this.showModal
			);
		},
		isModalVisibleInShopping() {
			return (
				!this.isInBudgetingPhase &&
				this.hasAnyLoanApplied &&
				this.hasToShowAnyLoan &&
				this.showModal
			);
		},
		loansAmount() {
			return {
				autoLoan: this.$options.filters.currency(
					this.lifeStageData.autoLoanDealershipAmount
				),
				autoLoanBankingAndFinance: this.$options.filters.currency(
					this.lifeStageData.autoLoanAmount
				),
				mortgageLoan: this.$options.filters.currency(
					this.lifeStageData.mortgageLoanAmount
				)
			};
		},
		isDisclaimerVisible() {
			// We are checking only for MortgageLoan because of AC
			return (
				this.$store.getters.isMortgageLoanVisible &&
				this.mortgageLoan.hasApplied &&
				!this.lifeStageData.isApprovedForMortgageLoan
			);
		},
		isCongratsVisible() {
			return (
				this.$store.getters.isAutoLoanDealershipVisible ||
				this.$store.getters.isAutoLoanFinanceVisible ||
				this.lifeStageData.isApprovedForMortgageLoan
			);
		}
	},
	watch: {
		options() {
			this.updateOptions();
		}
	},
	mounted() {
		if (this.isModalVisibleInShopping) {
			this.defaultOptions.hasTitle = true;
			this.defaultOptions.title = this.$t('loanApplicationStatus');
		}

		this.updateOptions();
	},
	methods: {
		closeModal() {
			if (this.isModalVisibleInShopping) {
				// It is an async request but since it not data-sensitive
				// we can run in a "background" while the game still will be playable
				this.$store.dispatch('updateLoansModalVisibility');
			}

			this.showModal = false;
			this.$emit('closeModal');
		},
		cancel() {
			this.$emit('cancel');
		},
		updateOptions() {
			this.defaultOptions = { ...this.defaultOptions, ...this.options };
		},
		getMessageDataForBudgeting(isAutoLoanApplied, isMortgageLoanApplied) {
			if (!isAutoLoanApplied && !isMortgageLoanApplied) {
				// Type is not required since it used as a placeholder in singular message,
				// not in the "plural" form
				// Count is used for "plural" form of the message( the message after "|" in translations )
				return {
					count: 2
				};
			} else if (!isMortgageLoanApplied) {
				return {
					type: 'mortgage',
					count: 1
				};
			}

			return {
				type: 'auto',
				count: 1
			};
		},
		getMessageDataForShopping(isAutoLoanApplied, isMortgageLoanApplied) {
			// LoanTypeStatus is a key for corresponding message in the translations
			// including "Congratulations text"
			if (
				this.$store.getters.isMortgageLoanVisible &&
				isMortgageLoanApplied &&
				this.lifeStageData.isApprovedForMortgageLoan &&
				this.$store.getters.isAutoLoanDealershipVisible &&
				this.$store.getters.autoLoan.hasApplied &&
				this.$store.getters.autoLoanBankingAndFinance.hasApplied
			) {
				return {
					loanTypeStatus: 'allLoans'
				};
			} else if (
				this.$store.getters.isMortgageLoanVisible &&
				isMortgageLoanApplied &&
				this.lifeStageData.isApprovedForMortgageLoan &&
				this.$store.getters.isAutoLoanDealershipVisible &&
				this.$store.getters.autoLoan.hasApplied &&
				!this.$store.getters.autoLoanBankingAndFinance.hasApplied
			) {
				return {
					loanTypeStatus: 'bothLoans'
				};
			} else if (
				this.$store.getters.isMortgageLoanVisible &&
				isMortgageLoanApplied &&
				this.lifeStageData.isApprovedForMortgageLoan &&
				this.$store.getters.isAutoLoanFinanceVisible &&
				!this.$store.getters.autoLoan.hasApplied &&
				this.$store.getters.autoLoanBankingAndFinance.hasApplied
			) {
				return {
					loanTypeStatus: 'bothLoansBanking'
				};
			} else if (
				this.$store.getters.isAutoLoanFinanceVisible &&
				this.$store.getters.autoLoan.hasApplied &&
				this.$store.getters.autoLoanBankingAndFinance.hasApplied
			) {
				return {
					loanTypeStatus: 'bothAutoLoan'
				};
			} else if (
				this.$store.getters.isAutoLoanFinanceVisible &&
				!this.$store.getters.autoLoan.hasApplied &&
				this.$store.getters.autoLoanBankingAndFinance.hasApplied
			) {
				return {
					loanTypeStatus: 'autoLoanBankingAndFinance'
				};
			} else if (
				this.$store.getters.isAutoLoanDealershipVisible &&
				this.$store.getters.autoLoan.hasApplied &&
				!this.$store.getters.autoLoanBankingAndFinance.hasApplied
			) {
				return {
					loanTypeStatus: 'autoLoan'
				};
			}

			return {
				loanTypeStatus: 'mortgageLoan'
			};
		}
	}
};
</script>
